/**
 * Runtime helpers for applying and previewing global form style presets.
 */

import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import { bridge } from '../editing/bridge.js';
import { assembleFormCSS } from '../renderer/css-assembler.js';
import { FORM_SHAPES, FORM_STYLES, FORM_SUBMIT_STYLES } from '../shared/forms-presets-data.js';

export { FORM_SHAPES, FORM_STYLES, FORM_SUBMIT_STYLES };

const PRESET_BY_KIND = {
  shape: FORM_SHAPES,
  style: FORM_STYLES,
  submit: FORM_SUBMIT_STYLES
};

function cloneFormStyles(formStyles) {
  return formStyles && typeof formStyles === 'object' ? { ...formStyles } : null;
}

function presetExists(kind, id) {
  return PRESET_BY_KIND[kind]?.some((preset) => preset.id === id) || false;
}

function mergeFormStyles(kind, id, baseStyles = store.getState()?.formStyles) {
  if (!presetExists(kind, id)) return cloneFormStyles(baseStyles);
  const next = { ...(baseStyles || {}) };
  next[kind] = id;
  return Object.keys(next).length ? next : null;
}

function applyFormPreset(kind, id, label) {
  const next = mergeFormStyles(kind, id);
  const didUpdate = store.update('formStyles', next, label);
  if (didUpdate) eventBus.emit('theme:override-changed');
}

function formPresetSnapshot(kind, id, label) {
  return {
    capture() {
      return cloneFormStyles(store.getState()?.formStyles);
    },
    apply() {
      bridge.send('update-form-styles', {
        css: assembleFormCSS(mergeFormStyles(kind, id))
      });
    },
    revert(captured) {
      bridge.send('update-form-styles', {
        css: assembleFormCSS(captured)
      });
    },
    commit() {
      applyFormPreset(kind, id, label);
    }
  };
}

export function applyFormShape(id) {
  applyFormPreset('shape', id, 'Apply form shape');
}

export function applyFormStyle(id) {
  applyFormPreset('style', id, 'Apply form style');
}

export function applyFormSubmit(id) {
  applyFormPreset('submit', id, 'Apply form submit');
}

export function formShapeSnapshot(id) {
  return formPresetSnapshot('shape', id, 'Apply form shape');
}

export function formStyleSnapshot(id) {
  return formPresetSnapshot('style', id, 'Apply form style');
}

export function formSubmitSnapshot(id) {
  return formPresetSnapshot('submit', id, 'Apply form submit');
}
